import {
  AssignmentRequestModel,
  BareCaseAssignmentWithTypeAndAssigneeNameModel,
} from 'api/model';
import { useCallback, useEffect, useMemo } from 'react';
import { useAssignmentRequestsGetApplicableAssignments } from 'api/assignment-requests/assignment-requests';
import useAuthenticationState from 'contexts/authentication/useAuthentication';
import MultiSelectAssignmentPicker from 'components/MultiSelectAssignmentPicker';
import useCompanies from 'contexts/basicData/useCompanies';
import useTranslations from 'contexts/basicData/useTranslations';
import { Option } from 'components/inputs/MultiSelect';
import { formatDateTime } from 'utils/date-helpers';

interface Props {
  request?: AssignmentRequestModel;
  disabled?: boolean;
  defaultSelected?: BareCaseAssignmentWithTypeAndAssigneeNameModel[];
  selected: BareCaseAssignmentWithTypeAndAssigneeNameModel[];
  onSelected(selected: BareCaseAssignmentWithTypeAndAssigneeNameModel[]): void;
}

const AssignmentRequestAssignmentPicker: React.FC<Props> = ({
  disabled,
  onSelected,
  selected,
  request,
  defaultSelected,
}) => {
  const { companiesById } = useCompanies();
  const { caseTypes, tyreTypes } = useTranslations();
  const {
    state: { isAuthenticated },
  } = useAuthenticationState();
  // api
  const suitableAssignmentsCall =
    useAssignmentRequestsGetApplicableAssignments();

  useEffect(() => {
    if (isAuthenticated && request?.requestedForID && request?.toCompanyID) {
      suitableAssignmentsCall.mutate({
        data: request,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    request?.requestedForID,
    request?.fromCompanyID,
    request?.toCompanyID,
    request?.targetStartTime,
    isAuthenticated,
  ]);

  const assignments = useMemo(() => {
    const suitable = (suitableAssignmentsCall.data ??
      []) as BareCaseAssignmentWithTypeAndAssigneeNameModel[];
    const result = [...(defaultSelected ?? [])];

    suitable.forEach((assignment) => {
      const alreadySelected = result.find(
        (a) => a.assignmentID === assignment.assignmentID
      );
      if (!alreadySelected) {
        result.push(assignment);
      }
    });

    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suitableAssignmentsCall.data]);

  const disabledIds = useMemo(() => {
    if (request?.fromCompanyID !== undefined) {
      const fromCompany = companiesById.get(request.fromCompanyID);
      const normalizeZip = (zip: string | undefined) =>
        (zip ?? '').replace(/\s/g, '');
      const expectedZip = normalizeZip(fromCompany?.zip);
      return assignments
        .filter((a) => normalizeZip(a.fromZip) !== expectedZip)
        .map((x) => x.assignmentID);
    }
    return [];
  }, [assignments, companiesById, request?.fromCompanyID]);

  const renderLabel = useCallback(
    (item: Option<number> | undefined) => {
      const model = assignments.find((a) => a.assignmentID === item?.value);
      if (!model) {
        return <span>-</span>;
      }
      return (
        <span>
          {`${model.caseRegistrationNumber} (${formatDateTime(model.bookedTo)}, ${model.fromCity} – ${model.toCity}) – ${caseTypes[model.caseType]}, ${tyreTypes[model.vehicleTyreType as number] ?? 'Okänd däcktyp'}`}
        </span>
      );
    },
    [assignments, caseTypes, tyreTypes]
  );

  if (!request) {
    return <span>Ange uppdragsförfrågan</span>;
  }

  return (
    <MultiSelectAssignmentPicker
      assignments={assignments}
      disabled={disabled}
      selected={selected}
      onSelected={onSelected}
      maxSelected={request.numberOfCars}
      disabledIds={disabledIds}
      renderLabel={renderLabel}
    />
  );
};

export default AssignmentRequestAssignmentPicker;
